import React, { useEffect } from 'react';
import { checkIsInIframe } from '@/utils';

/**
 * 兼容组件：将桌面端 Pointer 事件转为 Touch 事件，供 useZoom/useDragger 的触摸监听使用。
 * 仅在 iframe 场景、设备无原生触摸但支持 Pointer 的环境启用。
 * 同时在短按无位移时合成 click，保证点击选座可用。
 */
export default function SitCanvasCompat({ zoomTarget }: { zoomTarget: React.RefObject<HTMLElement> }) {
  useEffect(() => {
    // zoomTarget 当前已声明为 React.RefObject<HTMLElement>，直接取 current 即可
    const zoomEl = zoomTarget?.current;
    const supportTouchEvent = typeof window !== 'undefined' && 'ontouchstart' in window;
    const supportTouchPoints =
      typeof navigator !== 'undefined' &&
      'maxTouchPoints' in navigator &&
      navigator.maxTouchPoints > 0;
    const hasTouch = supportTouchEvent || supportTouchPoints;
    // @ts-ignore: 兼容低版本类型定义
    const hasPointer = typeof window.PointerEvent === 'function';
    const inIframe = checkIsInIframe();
    if (!zoomEl || hasTouch || !hasPointer || !inIframe) return;

    let isActive = false;
    const pointerId = 1;
    let startX = 0,
      startY = 0,
      startTime = 0,
      moved = false;
    const createTouchInit = (e: any) => {
      const rect = zoomEl.getBoundingClientRect();
      const clientX = e?.clientX ?? 0;
      const clientY = e?.clientY ?? 0;
      const pageX = clientX + window.scrollX;
      const pageY = clientY + window.scrollY;
      const touchLike = {
        identifier: pointerId,
        target: zoomEl,
        clientX,
        clientY,
        pageX,
        pageY,
        radiusX: 2.5,
        radiusY: 2.5,
        rotationAngle: 0,
        force: 0.5
      };
      const wraps = {
        touches: isActive ? [touchLike] : [],
        targetTouches: isActive ? [touchLike] : [],
        changedTouches: [touchLike],
        bubbles: true,
        cancelable: true,
        composed: true
      };
      return wraps;
    };

    const dispatchTouch = (type: 'touchstart' | 'touchmove' | 'touchend', init: any) => {
      try {
        // @ts-ignore: 兼容低版本类型定义
        const ev = new (window.TouchEvent)(type, init);
        zoomEl.dispatchEvent(ev);
      } catch (_err) {
        const ev = new CustomEvent(type, init);
        Object.assign(ev, {
          touches: init.touches || [],
          targetTouches: init.targetTouches || [],
          changedTouches: init.changedTouches || []
        });
        zoomEl.dispatchEvent(ev);
      }
    };

    const onPointerDown = (e: any) => {
      isActive = true;
      try {
        // @ts-ignore: 兼容低版本类型定义
        if (zoomEl.setPointerCapture) zoomEl.setPointerCapture(e?.pointerId || pointerId);
      } catch (_) {}
      startX = e?.clientX ?? 0;
      startY = e?.clientY ?? 0;
      startTime = Date.now();
      moved = false;
      const init = createTouchInit(e);
      // @ts-ignore
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      dispatchTouch('touchstart', init);
    };
    const onPointerMove = (e: any) => {
      if (!isActive) return;
      const cx = e?.clientX ?? 0;
      const cy = e?.clientY ?? 0;
      if (!moved) {
        const dx = Math.abs(cx - startX);
        const dy = Math.abs(cy - startY);
        if (dx > 6 || dy > 6) moved = true;
      }
      const init = createTouchInit(e);
      // @ts-ignore
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      dispatchTouch('touchmove', init);
    };
    const onPointerUp = (e: any) => {
      if (!isActive) return;
      isActive = false;
      try {
        // @ts-ignore
        if (zoomEl.releasePointerCapture) zoomEl.releasePointerCapture(e?.pointerId || pointerId);
      } catch (_) {}
      const init = createTouchInit(e);
      init.touches = [];
      init.targetTouches = [];
      // @ts-ignore
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      dispatchTouch('touchend', init);

      // 合成点击：短按且无位移时，主动触发 click 到命中元素
      const elapsed = Date.now() - startTime;
      if (!moved && elapsed < 300) {
        const clientX = e?.clientX ?? 0;
        const clientY = e?.clientY ?? 0;
        const targetEl = document.elementFromPoint(clientX, clientY);
        if (targetEl && targetEl instanceof HTMLElement) {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX,
            clientY,
            screenX: e?.screenX ?? 0,
            screenY: e?.screenY ?? 0
          });
          targetEl.dispatchEvent(clickEvent);
        }
      }
    };

    // @ts-ignore: 事件监听类型兼容
    zoomEl.addEventListener('pointerdown', onPointerDown, { passive: false });
    // @ts-ignore
    zoomEl.addEventListener('pointermove', onPointerMove, { passive: false });
    // @ts-ignore
    zoomEl.addEventListener('pointerup', onPointerUp, { passive: false });
    // @ts-ignore
    zoomEl.addEventListener('pointercancel', onPointerUp, { passive: false });

    return () => {
      // @ts-ignore
      zoomEl.removeEventListener('pointerdown', onPointerDown);
      // @ts-ignore
      zoomEl.removeEventListener('pointermove', onPointerMove);
      // @ts-ignore
      zoomEl.removeEventListener('pointerup', onPointerUp);
      // @ts-ignore
      zoomEl.removeEventListener('pointercancel', onPointerUp);
    };
  }, [zoomTarget]);

  return null;
}


